/**
 * @file 树模型
 * 注意：所有加载器类都是`Tree`类的友元类!
 */

import {
  STATE_UNCHECKED,
  STATE_CHECKED,
  STATE_INDETERMINATE,
  SENIORITY_SELF,
  SENIORITY_ANCESTOR,
  SENIORITY_DESCENDANT
} from './constants'

let seed = 0

/**
 * 具有层级结构的数据加载器，该加载器加载的数据具有父子层级关系。
 * @class
 */
// class DefaultLoader {
//   static load(data) {
//     this.clear()
//     // TODO: 待实现...
//   }
// }

/**
 * 列表数据加载器，该加载器加载的数据是一个平铺且乱序的队列结构。
 * @class
 */
class ListLoader {
  /**
   * 注册节点实例
   * @static
   * @private
   * @param {Tree} treeModel `Tree`类实例
   * @param {Array} data 树数据
   * @param {String} nodeKey 节点Id在树数据中对应的属性名
   * @param {String} subTreeRootId 子树跟节点Id
   */
  static _registerNodes(treeModel, data, nodeKey, subTreeRootId = null) {
    let node
    let roots = treeModel._roots
    let nodes = treeModel._nodes
    let regNodes = {}
    let count = data.length
    let lazyMode = treeModel.lazyMode
    let loadSubTree = !!subTreeRootId
    // 创建且注册节点对象实例
    for (let i = 0; i < count; i++) {
      node = new TreeNode({
        id: data[i].id,
        nodeKey: data[i].nodeKey ? data[i].nodeKey : nodeKey,
        pid: data[i].pid || subTreeRootId,
        label: data[i].label,
        expandedIcon: data[i].expandedIcon || treeModel.expandedIcon,
        foldedIcon: data[i].foldedIcon || treeModel.foldedIcon,
        leafIcon: data[i].leafIcon || treeModel.leafIcon,
        lazyIcon: data[i].lazyIcon || treeModel.lazyIcon,
        selected: data[i].selected,
        expanded: data[i].expanded,
        disabled: data[i].disabled,
        data: data[i].data,
        title: data[i].title
      })
      if (lazyMode === true) {
        if (data[i].lazy === true) { // 外部状态可以强制某个节点不进行懒加载
          node.lazy = true
        } else {
          node.lazy = false
        }
      }
      nodes[node.id] = node // 注册节点
      regNodes[node.id] = node
      if (loadSubTree === false) { // 加载子树时肯定已经存在根节点，因此不需要再注册它们
        if (node.isRoot) {
          roots.push(node) // 注册根节点
          node.rootId = node.id
          node.visibled = true
        }
      }
    }
    if (treeModel.comparer !== null) {
      treeModel._roots.sort(treeModel.comparer)
    }
    return regNodes
  }
  /**
   * 生成节点关联关系
   * @static
   * @private
   * @param {Tree} treeModel `Tree`类实例
   * @param {Object} regNodes 节点实例map
   */
  static _generateKinship(treeModel, regNodes) {
    let node
    let selectedNodes = treeModel._selectedNodes
    let nodes = treeModel._nodes
    // 生成节点父子关系
    for (let nodeId in regNodes) {
      node = regNodes[nodeId]
      if (treeModel.exist(node.pid)) {
        let parentNode = nodes[node.pid]
        if (Array.isArray(parentNode.children) === false) {
          parentNode.children = []
        }
        parentNode.children.push(node)
        if (node.selected) {
          parentNode.selectedCount++
          selectedNodes[node.id] = node
          treeModel._selectedCount++
        }
      } else {
        if (node.selected) {
          selectedNodes[node.id] = node
          treeModel._selectedCount++
        }
      }
    }
  }
  /**
   * 生成节点层级信息
   * @static
   * @private
   * @param {Tree} treeModel `Tree`类实例
   * @param {String} subTreeRootId 子树根节点Id
   */
  static _generateLevleInfo(treeModel, subTreeRootId = null) {
    let nodes = treeModel._nodes
    let levels = treeModel._levels
    // 生成层级信息，注意回调函数中的this上下文指向节点模型对象实例
    let cb = function(nodeId) {
      let parentNode = nodes[this.pid]
      if (this.isRoot === false) {
        this.level = parentNode.level + 1
        this.rootId = parentNode.rootId
      }
      let levelInfo = levels[this.level]
      if (!levelInfo) {
        levelInfo = levels[this.level] = {
          selectedCount: 0,
          count: 0,
          parents: {} // 放置本层节点的所有父节点实例
        }
      }
      if (this.selected) {
        levelInfo.selectedCount++
      }
      levelInfo.count++
      if (this.pid !== null) {
        levelInfo.parents[this.pid] = parentNode
      }
    }
    if (subTreeRootId) {
      treeModel.eachById(subTreeRootId, cb)
    } else {
      treeModel.each(cb)
    }
  }
  /**
   * 修正状态
   * @static
   * @private
   * @param {Tree} treeModel `Tree`类实例
   */
  static _fixStates(treeModel) {
    /*
     * 只有在 withAncestors 状态为 true 时才可能存在半选状
     * 态，此处修复存在半选状态时的选中状态，同时，将选中节点
     * 缓存 _selectedNodes 中漏掉的半选状态节点实例补上。
     */
    if (this.withAncestors === true) {
      let node
      let levels = treeModel._levels
      let selectedNodes = treeModel._selectedNodes
      let nodes = treeModel._nodes
      for (let i = levels.length - 1; i >= 1; i--) {
        let parents = levels[i].parents
        for (let nodeId in parents) {
          node = parents[nodeId]
          let oldSelected = node.selected
          node.updateState()
          // 修复非叶子节点可能产生的错误状态
          if (node.selected !== oldSelected) {
            let factor
            if (node.selected) {
              factor = 1
              selectedNodes[node.id] = node
            } else {
              factor = -1
              delete selectedNodes[node.id]
            }
            if (node.pid !== null) {
              nodes[node.pid].selectedCount += factor
            }

            this._selectedCount += factor
            levels[node.level].selectedCount += factor
          }
        }
      }
    }
  }
  /**
   * 加载子树
   * @param {String} subTreeRootId 子树根节点Id
   * @param {Array} data 树数据
   * @param {String} nodeKey 节点Id在树数据中对应的属性名
   */
  static loadSubTree(subTreeRootId, data, nodeKey) {
    this.clearDescendants(subTreeRootId)
    if (Array.isArray(data)) {
      this.loading = true
      let regNodes = ListLoader._registerNodes(this, data, nodeKey, subTreeRootId)
      ListLoader._generateKinship(this, regNodes)
      ListLoader._generateLevleInfo(this, subTreeRootId)
      ListLoader._fixStates(this)
      this.loading = false
    }
  }
  /**
   * 从数据中加载树节点，注意，该方法的`this`上下文对象指向一个`Tree`类实例
   * @param {Array} data 数组数据，该数组的每一个元素是一个`Object`，该`Object`对象可
   * @param {String} nodeKey data属性中指定的节点Key值所对应的属性名称。
   * 具有[TreeNode]{@link TreeNode}类构造时的所有选项，数组内每一项不需要按序排列。
   */
  static load(data, nodeKey) {
    this.clear()
    if (Array.isArray(data)) {
      this.loading = true
      let regNodes = ListLoader._registerNodes(this, data, nodeKey)
      ListLoader._generateKinship(this, regNodes)
      ListLoader._generateLevleInfo(this)
      ListLoader._fixStates(this)
      this.loading = false
    }
  }
}

/**
 * 树节点类
 * @class
 */
class TreeNode {
  /**
   * 创建`TreeNode`实例
   * @constructor
   * @param {Object} nodeState 节点状态选项
   * @param {String} [nodeState.nodeKey=''] data属性中指定的节点Key值所对应的属性名称。
   * @param {String} [nodeState.id] 节点Id，如果没有传入默认生成一个新Id。
   * @param {String} nodeState.pid 父节点Id。
   * @param {String} [nodeState.rootId=null] 根节点Id，如果没有传入默认为`null`。
   * @param {String} [nodeState.label=''] 节点标签文本。
   * @param {String} [nodeState.title=''] 节点提示文本。
   * @param {Number} nodeState.level 节点层级，根节点层级为`0`。
   * @param {Boolean} nodeState.selected 节点是否被选中，选中为`true`，否则为`false`。
   * @param {Boolean} nodeState.indeterminate 节点是否为半选，是半选为`true`（此时`selected`一定为`true`），否则为`false`。
   * @param {String} [nodeState.handleFoldedIcon=Tree.HANDLE_FOLDED_ICON] 节点收展控制收起图标样式类名。
   * @param {String} [nodeState.handleExpandedIcon=Tree.HANDLE_EXPANDED_ICON] 节点收展控制展开图标样式类名。
   * @param {String} [nodeState.handleFoldedIcon2=Tree.HANDLE_FOLDED_ICON2] 节点收展控制收起图标样式类名，此图标在辅助线显示时使用。
   * @param {String} [nodeState.handleExpandedIcon2=Tree.HANDLE_EXPANDED_ICON2] 节点收展控制展开图标样式类名，此图标在辅助线显示时使用。
   * @param {String} [nodeState.foldedIcon=Tree.FOLDED_ICON] 折叠状态时的图标样式类名。
   * @param {String} [nodeState.expandedIcon=Tree.EXPANDED_ICON] 展开状态时的图标样式类名。
   * @param {String} [nodeState.leafIcon=Tree.LEAF_ICON] 无子节点状态（叶子节点）时的图标样式类名。
   * @param {String} [nodeState.lazyIcon=Tree.LAZY_ICON] 懒加载节点状态时的图标样式类名。
   * @param {String} [nodeState.loadingAni=Tree.LOADING_ANT] 节点加载状态时的图标样式类名。
   * @param {*} [nodeState.data] 绑定到节点的业务数据。
   * @param {Array} [nodeState.children=null] 子节点数组。
   * @param {Array} [nodeState.selectedCount=0] 子节点被选中总数。
   * @param {Array} [nodeState.lazy=false] 是否是懒加载节点，如果为`true`则表示此节点可能存在子节点但是还没有加载，否则为`false`。
   * @param {Array} [nodeState.loading=false] 是否正在加载子节点信息，如果为`true`则表示此节点正在加载子节点信息，否则为`false`。
   * @param {Boolean} [nodeState.visibled=false] 是否可见，如果为`true`则表示此节点可见，否则为`false`。
   * @param {Boolean} [nodeState.expanded=false] 是否展开，如果为`true`则表示此节点已经展开，否则为`false`。
   * @param {Boolean} [nodeState.disabled=true] 是否可用，如果为`true`则表示此节点可用，否则为`false`。
   */
  constructor (nodeState) {
    this.data = nodeState.data || null
    this._nodeKey = nodeState.nodeKey
    if (this.data &&
        typeof this.data === 'object' &&
        this.nodeKey in this.data) {
      this.id = this.data[this.nodeKey]
    } else {
      this.id = nodeState.id || `n${seed++}`
    }
    this.pid = nodeState.pid || null
    this.rootId = nodeState.rootId || null
    this.label = nodeState.label || ''
    this.title = nodeState.title || ''
    this.level = isNaN(nodeState.level) ? 0 : parseInt(nodeState.level, 10)
    this.selected = !!nodeState.selected
    this.indeterminate = !!nodeState.indeterminate
    this.handleFoldedIcon = nodeState.handleFoldedIcon || Tree.HANDLE_FOLDED_ICON
    this.handleExpandedIcon = nodeState.handleExpandedIcon || Tree.HANDLE_EXPANDED_ICON
    this.handleFoldedIcon2 = nodeState.handleFoldedIcon2 || Tree.HANDLE_FOLDED_ICON2
    this.handleExpandedIcon2 = nodeState.handleExpandedIcon2 || Tree.HANDLE_EXPANDED_ICON2
    this.foldedIcon = nodeState.foldedIcon || ''
    this.expandedIcon = nodeState.expandedIcon || ''
    this.leafIcon = nodeState.leafIcon || ''
    this.lazyIcon = nodeState.lazyIcon || ''
    this.loadingAni = nodeState.loadingAni || Tree.LOADING_ANI
    this.children = nodeState.children || null
    this.selectedCount = isNaN(nodeState.selectedCount) ? 0 : parseInt(nodeState.selectedCount, 10)
    this.lazy = !!nodeState.lazy
    this.loading = !!nodeState.loading
    this.visibled = !!nodeState.visibled
    this.disabled = !!nodeState.disabled
    this.expanded = !!nodeState.expanded
    // 辈份标志，它在调用setSelected方法后将被设置以区分返回节点状态是自己还是祖先还是子孙：
    // 0 -- 自己; 1 -- 祖先; 2 -- 子孙
    this._seniority = SENIORITY_SELF
    this._sorted = false // 子节点是否已经排序
    this.rowVisiable = true
    this.searchLabel = false
    this.visiableChildCount = 0
    this.lineLight = false
  }
  /**
   * 获得节点的当前状态
   * @returns {Object} 返回节点的当前状态，参见[TreeNode类的构造函数参数]{@link TreeNode}，注意：`children`属性将仅返回子节点的`id`属性值数组。
   */
  get state() {
    let state = {
      id: this.id,
      pid: this.pid,
      rootId: this.rootId,
      label: this.label,
      title: this.title,
      level: this.level,
      selected: this.selected,
      indeterminate: this.indeterminate,
      foldedIcon: this.foldedIcon,
      expandedIcon: this.expandedIcon,
      leafIcon: this.leafIcon,
      lazyIcon: this.lazyIcon,
      loadingAni: this.loadingAni,
      data: this.data,
      children: null,
      lazy: this.lazy,
      loading: this.loading,
      visibled: this.visibled,
      disabled: this.disabled,
      expanded: this.expanded,
      seniority: this._seniority,
      rowVisiable: this.rowVisiable,
      searchLabel: this.searchLabel,
      visiableChildCount: this.visiableChildCount,
      lineLight: this.lineLight
    }
    if (Array.isArray(this.children)) {
      state.children = this.children.map(item => {
        return item.id
      })
    }
    return state
  }
  /**
   * 销毁实例
   */
  destroy() {
    this.children = null
    this.data = null
  }
  get nodeKey() {
    return this._nodeKey
  }
  get visibled() {
    return this._visibled
  }
  set visibled(val) {
    this._visibled = !!val
  }
  /** 修改 */
  get visiableChildCount() {
    return this._visiableChildCount
  }
  set visiableChildCount(val) {
    this._visiableChildCount = val
  }
  get rowVisiable() {
    return this._rowVisiable
  }
  set rowVisiable(val) {
    this._rowVisiable = !!val
  }
  set searchLabel(val) {
    this._searchLabel = !!val
  }
  set lineLight(val) {
    this._lineLight = val
  }
  get lineLight() {
    return this._lineLight
  }
  /** 修改 */
  get disabled() {
    return this._disabled
  }
  set disabled(val) {
    this._disabled = !!val
  }
  /**
   * 获得子节点总数
   * @returns {Number} 返回当前节点的子节点总数
   */
  get childCount() {
    return Array.isArray(this.children) ? this.children.length : 0
  }
  /**
   * 检测是否是根节点
   * @returns {Boolean} 如果是根节点返回`true`，否则返回`false`
   */
  get isRoot() {
    return (this.pid === null)
  }
  /**
   * 检测是否是叶子节点
   * @returns {Boolean} 如果是有子节点返回`true`，否则返回`false`
   */
  get isLeaf() {
    return this.childCount === 0
  }
  /**
   * 更新节点状态
   */
  updateState() {
    let children = this.children
    let state = null
    for (let i = this.childCount - 1; i >= 0; i--) {
      if (children[i].indeterminate) {
        state = STATE_INDETERMINATE
        break
      }
    }
    if (state === null) {
      if (this.childCount === this.selectedCount) {
        state = STATE_CHECKED
      } else if (this.selectedCount === 0) {
        state = STATE_UNCHECKED
      } else {
        state = STATE_INDETERMINATE
      }
    }
    this.indeterminate = (state === STATE_INDETERMINATE)
    this.selected = (state !== STATE_UNCHECKED)
  }
  /**
   * DFS遍历节点
   * @param {Tree} tree 树对象实例
   * @param {Function} [before=null] 迭代前回掉函数
   * @param {Function} [after=null] 迭代后回调函数
   * @param {Boolean} [withAncestors=false] 是否遍历祖先节点。如果在遍历子树节点时不需要遍历该节点的祖先节点，那么将其设置为`false`，否则设置为`true`。
   * @param {Boolean} [withDescendants=true] 是否遍历子孙节点。如果在遍历子树节点时不需要遍历该节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   */
  each(tree, before = null, after = null, withAncestors = false, withDescendants = true) {
    if (tree instanceof Tree && tree.exist(this.id)) {
      let nodes = tree.nodes
      let nd = this
      let node = nodes[this.id]
      if (node.children)node.visiableChildCount = node.childCount
      if (typeof before === 'function') {
        before.call(this, this.id, tree)
      }
      if (withAncestors) {
        nd = this._nodes[nd.pid]
        while (nd) {
          nd.each(tree, before, after, withAncestors, withDescendants)
          nd = nodes[nd.pid]
        }
      }
      if (withDescendants && this.childCount > 0) {
        let children = this.children
        for (let i = children.length - 1; i >= 0; i--) {
          children[i].each(tree, before, after, withAncestors, withDescendants)
        }
      }
      if (typeof after === 'function') {
        after.call(this, this.id)
      }
    }
  }
  /**
   * 设置节点的选中状态
   * @param {Tree} tree `Tree`类实例
   * @param {Boolean} selected 选中状态，`true`表示选中，`false`表示取消选中
   * @param {Boolean} [withAncestors=false] 是否遍历祖先节点。如果在遍历子树节点时不需要遍历该节点的祖先节点，那么将其设置为`false`，否则设置为`true`。
   * @param {Boolean} [withDescendants=true] 是否遍历子孙节点。如果在遍历子树节点时不需要遍历该节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   * @returns {Object} 返回一个对象，该对象包含所有受影响的节点，节点的Id作为key，节点实例作为值
   */
  setSelected(tree, selected = true, withAncestors = false, withDescendants = true) {
    let affectedNodes = {}
    if (tree.multiple) {
      if (this.selected !== selected) {
        let nodes = tree.nodes
        let factor = selected ? 1 : -1
        this.selected = selected
        this.indeterminate = false
        affectedNodes[this.id] = this
        Tree.mutateLevelSelectCount(tree, this.level, factor)
        if (this.isRoot === false) {
          let nd = nodes[this.pid]
          nd.selectedCount += factor
          if (withAncestors) {
            while (nd) {
              nd._seniority = SENIORITY_ANCESTOR
              let changed = false
              let oldSelected = nd.selected
              let oldIndeterminate = nd.indeterminate
              nd.updateState()
              factor = nd.selected ? 1 : -1
              if (nd.selected !== oldSelected) {
                changed = true
                Tree.mutateLevelSelectCount(tree, nd.level, factor)
                if (nodes[nd.pid]) {
                  nodes[nd.pid].selectedCount += factor
                }
              }
              if (nd.indeterminate !== oldIndeterminate) {
                changed = true
              }
              if (changed) {
                affectedNodes[nd.id] = nd
              }
              nd = nodes[nd.pid]
            }
          }
        }
        if (withDescendants && this.childCount > 0) {
          let children = this.children
          for (let i = 0, count = children.length; i < count; i++) {
            let affectedChildNodes = children[i].setSelected(tree, selected, false)
            for (let nodeId in affectedChildNodes) {
              affectedChildNodes[nodeId]._seniority = SENIORITY_DESCENDANT
              affectedNodes[nodeId] = affectedChildNodes[nodeId]
            }
          }
        }
      }
    } else {
      this.selected = selected
      affectedNodes[this.id] = this
    }
    let pNode = tree._nodes[this.pid]
    while (pNode) {
      pNode.expand()
      pNode = tree._nodes[pNode.pid]
    }
    return affectedNodes
  }
  /**
   * 展开节点
   * @param {Boolean} [withDescendants=false] 是否遍历子孙节点。如果在遍历子树节点时不需要遍历该节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   */
  expand(withDescendants = false) {
    if (this.childCount > 0 && this.expanded === false) {
      let children = this.children
      if (this._sorted === false && this.comparer !== null) {
        this._sorted = true
        children.sort(this.comparer)
      }
      this.expanded = true
      // this.expendLose && (this.expanded = true)
      if (withDescendants) {
        for (let i = 0, count = children.length; i < count; i++) {
          children[i].rowVisiable && (children[i].visibled = true)
          children[i].expand(withDescendants)
        }
      } else {
        for (let i = 0, count = children.length; i < count; i++) {
          children[i].rowVisiable && (children[i].visibled = true)
        }
      }
    }
  }
  /**
   * 收起节点
   * @param {Boolean} [withDescendants=false] 是否遍历子孙节点。如果在遍历子树节点时不需要遍历该节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   */
  fold(withDescendants = false) {
    if (this.childCount > 0 && this.expanded === true) {
      this.expanded = false
      let children = this.children
      if (withDescendants) {
        for (let i = 0, count = children.length; i < count; i++) {
          children[i].visibled = false
          children[i].fold(withDescendants)
        }
      } else {
        for (let i = 0, count = children.length; i < count; i++) {
          children[i].visibled = false
        }
      }
    }
  }
}

/**
 * 树类
 * @class
 */
class Tree {
  /**
   * 创建`Tree`实例
   * @constructor
   * @param {Object} options 节点状态选项
   * @param {String} [options.handleFoldedIcon=Tree.HANDLE_FOLDED_ICON] 节点收展控制收起图标样式类名。
   * @param {String} [options.handleExpandedIcon=Tree.HANDLE_EXPANDED_ICON] 节点收展控制展开图标样式类名。
   * @param {String} [options.handleFoldedIcon2=Tree.HANDLE_FOLDED_ICON2] 节点收展控制收起图标样式类名，此图标在辅助线显示时使用。
   * @param {String} [options.handleExpandedIcon2=Tree.HANDLE_EXPANDED_ICON2] 节点收展控制展开图标样式类名，此图标在辅助线显示时使用。
   * @param {String} [options.foldedIcon=Tree.FOLDED_ICON] 节点折叠状态时的图标样式类名。
   * @param {String} [options.expandedIcon=Tree.EXPANDED_ICON] 节点展开状态时的图标样式类名。
   * @param {String} [options.leafIcon=Tree.LEAF_ICON] 节点无子节点状态（叶子节点）时的图标样式类名。
   * @param {String} [options.lazyIcon=Tree.LAZY_ICON] 懒加载节点状态时的图标样式类名。
   * @param {String} [options.loadingAni=Tree.LOADING_ANT] 节点加载状态时的图标样式类名。
   * @param {Function} [options.lazyLoader=null] 延迟加载回调函数，如果指定了该函数，那么默认情况下所有叶子节点都将是懒加载节点（即：其节点实例的`lazy`属性为`true`）。
   * @param {Function} [options.comparer=null] 节点排序回调函数，如果指定了该函数，那么在节点展开节点时会对该节点的子节点调用该排序函数进行一次排序操作，后续再次展开时不再执行排序操作。
   * @param {Boolean} [options.multiple=false] 是否是多选模式。
   * @param {Boolean} [options.withAncestors=false] 是否关联祖先节点。如果在遍历子树节点时不需要遍历该子树根节点的祖先节点，那么将其设置为`false`，否则设置为`true`。
   * @param {Boolean} [options.withDescendants=false] 是否关联子孙节点。如果在遍历子树节点时不需要遍历该子树根节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   */
  constructor (options) {
    options = options || {}
    this.handleFoldedIcon = options.handleFoldedIcon || Tree.HANDLE_FOLDED_ICON
    this.handleExpandedIcon = options.handleExpandedIcon || Tree.HANDLE_EXPANDED_ICON
    this.handleFoldedIcon2 = options.handleFoldedIcon2 || Tree.HANDLE_FOLDED_ICON2
    this.handleExpandedIcon2 = options.handleExpandedIcon2 || Tree.HANDLE_EXPANDED_ICON2
    if (typeof options.foldedIcon === 'string') {
      this.foldedIcon = options.foldedIcon
    } else {
      this.foldedIcon = Tree.FOLDED_ICON
    }
    if (typeof options.expandedIcon === 'string') {
      this.expandedIcon = options.expandedIcon
    } else {
      this.expandedIcon = Tree.EXPANDED_ICON
    }
    if (typeof options.leafIcon === 'string') {
      this.leafIcon = options.leafIcon
    } else {
      this.leafIcon = Tree.LEAF_ICON
    }
    if (typeof options.lazyIcon === 'string') {
      this.lazyIcon = options.lazyIcon
    } else {
      this.lazyIcon = Tree.LAZY_ICON
    }
    this.loadingAni = options.loadingAni || Tree.LOADING_ANI
    this.multiple = !!options.multiple
    this.lazyLoader = (typeof options.lazyLoader === 'function') ? options.lazyLoader : null
    this.comparer = (typeof options.comparer === 'function') ? options.comparer : null
    this.withAncestors = !!options.withAncestors
    this.withDescendants = !!options.withDescendants
    this._roots = [] // 根节点数组
    this._nodes = {} // 所有子节点数组
    this._levels = [] // 每一层级包含的节点二维数组
    this._selectedNodes = {} // 选中的节点
    this._selectedCount = 0 // 选中节点总数
    this._switchEmpty = false
    /**
     * 树实例加载节点的加载器，一个加载器是一个具有`load`方法的对象。
     * @property
     * @name loader
     */
    this._loader = ListLoader
    /**
     * 是否正在加载，如果当前树实例正在加载返回`true`，否则返回`false`
     * @property
     * @name loading
     */
    this._loading = false
  }
  static get HANDLE_FOLDED_ICON() {
    return 'menu-right-outline'
  }
  static get HANDLE_EXPANDED_ICON() {
    return 'menu-down-outline'
  }
  static get HANDLE_FOLDED_ICON2() {
    return 'plus-box-outline'
  }
  static get HANDLE_EXPANDED_ICON2() {
    return 'minus-box-outline'
  }
  static get FOLDED_ICON() {
    return 'folder-outline'
  }
  static get EXPANDED_ICON() {
    return 'folder-open-outline'
  }
  static get LAZY_ICON() {
    return 'folder-add-outline'
  }
  static get LEAF_ICON() {
    return 'file-document-outline'
  }
  get withAncestors() {
    return this._withAncestors
  }
  set withAncestors(val) {
    if (this.multiple) {
      this._withAncestors = !!val
    } else {
      this._withAncestors = false
    }
  }
  get withDescendants() {
    return this._withDescendants
  }
  set withDescendants(val) {
    if (this.multiple) {
      this._withDescendants = !!val
    } else {
      this._withDescendants = false
    }
  }
  get loading() {
    return this._loading
  }
  set loading(val) {
    this._loading = !!val
  }
  get loader() {
    return this._loader
  }
  set loader(val) {
    if (val !== null &&
        typeof val === 'object' &&
        typeof val.load === 'function') {
      this._loader = val
    }
  }
  get selectedCount() {
    return this._selectedCount
  }
  get lazyMode() {
    return typeof this.lazyLoader === 'function'
  }
  /**
   * 检测给定的节点Id是否是树中的节点
   * @param {String} nodeId 节点Id
   * @returns {Boolean} 如果节点Id在树中存在返回`true`，否则返回`false`
   */
  exist(nodeId) {
    return (nodeId in this._nodes)
  }
  /**
   * 检测给定的节点Id是否是懒加载节点
   * @param {String} nodeId 节点Id
   * @returns {Boolean} 如果是懒加载节点返回`true`，否则返回`false`
   */
  isLazy(nodeId) {
    return this.exist(nodeId) ? this._nodes[nodeId].lazy : false
  }
  /**
   * 获得所有节点的状态
   * @returns {Array} 返回所有节点的状态对象数组
   */
  get allNodeStates() {
    let arr = []
    for (let nodeId in this._nodes) {
      arr.push(this._nodes[nodeId].state)
    }
    return arr
  }
  /**
   * 获得所有根节点的状态
   * @returns {Array} 返回所有根节点的状态
   */
  get allRootStates() {
    let arr = this._roots.map(item => {
      return item.state
    })
    return arr
  }
  /**
   * 获得所有选中节点的状态
   * @returns {Array} 返回所有选中节点的状态对象数组
   */
  get selectedNodeStates() {
    let selectedNodes = this._selectedNodes
    let states = []
    for (let nodeId in selectedNodes) {
      states.push(selectedNodes[nodeId].state)
    }
    return states
  }
  /**
   * 获得所有选中节点实例
   * @returns {Object} 返回所有选中节点的实例
   */
  get selectedNodes() {
    return Object.assign({}, this._selectedNodes)
  }
  /**
   * 获得所有选中节点Id
   * @returns {Array} 返回所有选中节点Id
   */
  get selectedNodeIds() {
    return Object.keys(this._selectedNodes)
  }
  /**
   * 根据节点Id获得节点状态
   * @param {String} nodeId 节点Id
   * @returns {Object|null} 如果节点存在则返回该节点状态对象，否则返回`null`
   */
  getNodeStateById(nodeId) {
    if (this.exist(nodeId)) {
      return this._nodes[nodeId].state
    }
    return null
  }
  /**
   * 根据节点Id获得节点对象实例
   * @param {String} nodeId 节点Id
   * @returns {TreeNode|null} 如果节点Id在当前树中存在返回该节点Id对应的节点对象实例，否则返回`null`
   */
  getNodeById(nodeId) {
    if (this.exist(nodeId)) {
      return this._nodes[nodeId]
    }
    return null
  }
  expand(nodeId) {
    if (this.exist(nodeId)) {
      let nodeModel = this.getNodeById(nodeId)
      let withDescendants = this.withDescendants
      if (this.lazyMode && nodeModel.lazy === true) {
        nodeModel.loading = true
        nodeModel.lazy = false
        return this.lazyLoader(nodeModel.state).then((data) => {
          this.reloadSubTree(nodeId, data)
          nodeModel.loading = false
          nodeModel.expand(withDescendants)
          return Promise.resolve(nodeId)
        })
      } else {
        nodeModel.expand(withDescendants)
        return Promise.resolve(nodeId)
      }
    }
  }
  /**
   * 清除指定节点的子孙节点
   */
  clearDescendants(nodeId) {
    let nodeModel = this.getNodeById(nodeId)
    if (nodeModel.isLeaf === false) {
      let children = nodeModel.children
      for (let i = children.length - 1; i >= 0; i--) {
        this.deleteNode(children[i].id)
      }
    }
  }
  /**
   * 清除所有节点
   */
  clear() {
    this._roots = []
    this._levels = []
    this._selectedNodes = {}
    this._selectedCount = 0
    let nodes = this._nodes
    for (let nodeId in nodes) {
      nodes[nodeId].destroy()
    }
    this._nodes = {}
  }
  /**
   * 节点状态校验器
   * @private
   * @param {Object} nodeState 节点状态
   * @returns {Boolean} 如果校验成功返回`true`，否则返回`false`
   */
  _nodeStateValidator(nodeState) {
    if (nodeState.id && this.exist(nodeState.id) === true) {
      return false
    }
    if (nodeState.pid && this.exist(nodeState.pid) === false) {
      return false
    }
    return true
  }
  /**
   * 添加节点
   * @param {Object} nodeState 节点状态对象
   */
  addNode(nodeState) {
    let nodes = this._nodes
    let levels = this._levels
    let levelInfo
    let newNode
    let parentNode

    if (this._nodeStateValidator(nodeState) === true) {
      newNode = new TreeNode(nodeState)
      levelInfo = levels[newNode.level]
      if (!levelInfo) {
        levelInfo = levels[newNode.level] = {
          selectedCount: 0,
          count: 0,
          parents: {} // 放置本层节点的所有父节点实例
        }
      }
      if (newNode.isRoot) {
        newNode.level = 0
        newNode.rootId = newNode.id // 根节点的rootId属性与id属性相同以便继承到子节点
        this._roots.push(newNode)
        if (this.comparer !== null) {
          this._roots.sort(this.comparer)
        }
      } else {
        parentNode = nodes[newNode.pid]
        newNode.level = parentNode.level + 1
        newNode.rootId = parentNode.rootId
        levelInfo.parents[newNode.pid] = parentNode
        if (parentNode.childCount === 0) {
          parentNode.children = []
        }
        parentNode.children.push(newNode)
        if (newNode.selected === true) {
          levelInfo.selectedCount++
          parentNode.selectedCount++
        }
        this._updateAncestorStates(parentNode)
        if (this.comparer !== null) {
          parentNode._sorted = true
          parentNode.children.sort(this.comparer)
        }
      }
      nodes[newNode.id] = newNode
      return newNode.state
    }
    return null
  }
  /**
   * 更新祖先节点状态
   * @private
   * @param {TreeNode} 节点对象实例
   */
  _updateAncestorStates(node) {
    let nd = node
    let nodes = this._nodes
    while (nd) {
      nd.updateState()
      nd = nodes[nd.pid]
    }
  }
  /**
   * 删除节点
   * @param {String} nodeId 节点Id
   */
  deleteNode(nodeId) {
    let nodes = this._nodes
    let levels = this._levels
    let selectedNodes = this._selectedNodes
    let node
    let parentNode
    if (this.exist(nodeId)) { // 判断输入id是否存在
      node = nodes[nodeId] // 当前节点对象
      let roots = this._roots
      for (let i = roots.length - 1; i >= 0; i--) {
        if (roots[i].id === nodeId) {
          this._roots.splice(i, 1) // 判断当前节点是不是根节点
          break
        }
      }
      // 如果当前节点不是根节点
      if (node.pid !== null) {
        parentNode = nodes[node.pid] // 找到当前节点的直接父元素
        if (this._spliceChildren(parentNode.children, nodeId)) {
          if (node.selected) { // 标识当前是否选中状态
            parentNode.selectedCount-- // 标识其父元素是圆角还是半角
          }
        }
        if (parentNode.childCount === 0) {
          parentNode.selectedCount = 0
          parentNode.lazy = false
          parentNode.loading = false
        }
      }
      let deleteNodes = []
      let self = this
      let cb = function(nodeId) {
        let levelInfo = levels[this.level] // 获取节点信息
        if (this.selected === true) {
          levelInfo.selectedCount--
          self._selectedCount--
          delete selectedNodes[this.id]
        }
        if (this.id in levelInfo.parents) {
          delete levelInfo.parents[this.id]
        }
        deleteNodes.push(this)
      }
      node.each(this, cb) // 遍历子树节点
      // 释放节点实例，先删后代最后删除自己
      for (let i = deleteNodes.length - 1; i >= 0; i--) {
        deleteNodes[i].destroy()
        delete nodes[deleteNodes[i].id]
      }
      // 遍历祖先节点，更新节点状态
      node.each(this, function (nodeId) {
        this.updateState()
      }, null, true)
    }
  }
  /**
   * 从指定的子节点数组中去掉指定的节点对象实例
   * @private
   * @param {Array} children 子节点数组
   * @param {String} nodeId 节点Id
   * @returns {Boolean} 如果去除成功返回`true`，否则返回`false`
   */
  _spliceChildren(children, nodeId) {
    for (let i = children.length - 1; i >= 0; i--) {
      if (children[i].id === nodeId) {
        children.splice(i, 1)
        return true
      }
    }
    return false
  }
  /**
   * 获得所有节点对象实例
   * @returns {Object} 返回一个对象，该对象包含所有节点对象实例，它以每个节点的Id作为key。
   */
  get nodes() {
    return Object.assign({}, this._nodes)
  }
  /**
   * 获得所有根节点对象实例
   * @returns {Object} 返回一个对象，该对象包含所有根节点对象实例，它以每个根节点的Id作为key。
   */
  get roots() {
    return this._roots.concat()
  }
  /**
   * DFS遍历树中所有节点
   * @param {Function} [before=null] 迭代前回掉函数
   * @param {Function} [after=null] 迭代后回调函数
   */
  each(before = null, after = null) {
    this._roots.forEach(item => {
      item.each(this, before, after)
    })
  }
  /**
   * 从指定节点开始进行DFS遍历
   * @param {String} nodeId 节点Id
   * @param {Function} [before=null] 迭代前回掉函数
   * @param {Function} [after=null] 迭代后回调函数
   * @param {Boolean} [withAncestors=false] 是否遍历祖先节点。如果在遍历时不需要遍历该节点的祖先节点，那么将其设置为`false`，否则设置为`true`。
   * @param {Boolean} [withDescendants=true] 是否遍历子孙节点。如果在遍历时不需要遍历该节点的子孙节点，那么将其设置为`false`，否则设置为`true`。
   */
  eachById(nodeId, before = null, after = null, withAncestors = false, withDescendants = true) {
    if (this.exist(nodeId)) {
      this._nodes[nodeId].each(this, before, after, withAncestors, withDescendants)
    }
  }
  /**
   * 从业务数据重新加载树
   * @param {Array|Object} data 业务数据
   * @param {String} [nodeKey] 节点唯一标识在数据中的属性名称
   */
  reload(data, nodeKey) {
    this.loader.load.call(this, data, nodeKey)
  }
  reloadSubTree(nodeId, data, nodeKey) {
    this.loader.loadSubTree.call(this, nodeId, data, nodeKey)
  }
  get isEmpty() {
    return this._roots.length === 0
  }
  get switchEmpty() {
    return this._switchEmpty
  }
  set switchEmpty(val) {
    this._switchEmpty = val
  }
  /**
   * 变更选中节点数量
   * @param {Tree} treeInstance 树实例
   * @param {Number} level 级别序号
   * @param {Number} payload 变更的选中节点增量数，可以为负数。
   */
  static mutateLevelSelectCount(treeInstance, level, payload) {
    if (level in treeInstance._levels) {
      treeInstance._levels[level].selectedCount += payload
    }
  }
  setSelected(nodeId, wantSelected = true) {
    if (this.exist(nodeId)) {
      let selectedNodes = this._selectedNodes
      // 获取所点击checkout的本身及其父元素或子元素
      let affectedNodes = this._nodes[nodeId].setSelected(this, wantSelected, this.withAncestors, this.withDescendants)
      if (wantSelected) {
        for (let nodeId in affectedNodes) {
          if (!(nodeId in selectedNodes)) {
            selectedNodes[nodeId] = affectedNodes[nodeId]
            this._selectedCount++
          }
        }
      } else {
        for (let nodeId in affectedNodes) {
          if (nodeId in selectedNodes && affectedNodes[nodeId].indeterminate === false) {
            delete selectedNodes[nodeId]
            this._selectedCount--
          }
        }
      }
    }
  }
  /**
   * 获取筛选节点所对应的父元素的id,且返回对应数组
   * @param {String} nodeId 节点ID值
   */
  getrelParent(nodeId) {
    let nodes = this._nodes
    let roots = this._roots
    let node
    let nodesList = [nodeId]
    let parentNode
    if (this.exist(nodeId)) {
      node = nodes[nodeId]
      if (node.pid === null) {
        for (let i = roots.length - 1; i >= 0; i--) {
          if (roots[i].id === nodeId) {
            nodesList = nodeId
            break
          }
        }
      } else {
        if (node.pid !== null) {
          parentNode = nodes[node.pid]
          nodesList.push(parentNode.id)
          while (parentNode.pid !== null) {
            parentNode = nodes[parentNode.pid]
            nodesList.push(parentNode.id)
          }
        }
      }
      return nodesList
    }
  }
  setSwitchEmpty(val) {
    this.switchEmpty = val
  }
  /**
   * 输入为空时初始化
   */
  filterInit() {
    let node = this._nodes
    this.each(function(id, tree) {
      /** 给选中的节点添加class */
      node[id].searchLabel = false
      node[id].expanded = false
      node[id].rowVisiable = true
      node[id].visibled = true
      node[id].selected = false
      node[id].selectedCount = 0
      tree.switchEmpty = false
    })
  }
  filterNodes(nodeIdList) {
    let visiableShowList = []
    let visiablehidList = []
    let node = this._nodes
    /** 初始化 */
    this.filterInit()
    if (nodeIdList) {
      if (nodeIdList.length !== 0) {
        nodeIdList.forEach(item => {
          let currentParent = this.getrelParent(item) // 获取传入元素的所有父节点
          visiableShowList.push(currentParent)
          node[item].searchLabel = true // 给当前选中的节点添加class名
        })
      } else {
        this.switchEmpty = true
        return
      }
      visiableShowList = new Set(visiableShowList.flat())
      /** 获取所要显示的所有节点（包括筛选出来的节点及其父节点） */
      visiableShowList = Array.from(visiableShowList)
      this.each(function(id, tree) {
        /** 初始化所有rowVisiable的值 */
        node[id].rowVisiable = true
        if (!visiableShowList.includes(id)) {
          visiablehidList.push(id)
        }
      })
      /** 让所有需要隐藏的节点隐藏 */
      visiablehidList.length && visiablehidList.forEach(item => {
        let currendNode = node[item]
        /** 让未选中的子节点无法点击 */
        currendNode.rowVisiable = false
        /** 让未选中的子元素隐藏 */
        currendNode.visibled = false
        if (currendNode.pid && currendNode.visibled === false) {
          node[currendNode.pid].visiableChildCount--
        }
      })
      /** 让所有需要显示的节点显示 */
      visiableShowList.length && visiableShowList.forEach(item => {
        node[item].visibled = true
        if (node[item].children) {
          node[item].expanded = true
        }
      })
    }
  }
  /**
   * 销毁实例
   */
  destroy() {
    let nodes = this._nodes
    for (let nodeId in nodes) {
      nodes[nodeId].destroy()
    }
    this.lazyLoader = null
    this._nodes = null
    this._roots = null
    this._levels = null
    this._selectedNodes = null
  }
}

export {
  TreeNode,
  Tree
}



// WEBPACK FOOTER //
// ./src/module/tree/tree-model.js